import React, { useEffect } from 'react';
import { TitleBar } from './TitleBar';
import { useTranslation } from 'react-i18next';

// Props are the same as Standard PageLayout for now
interface FixedChatLayoutProps {
  children: React.ReactNode;
  withPadding?: boolean; // Note: Padding might apply differently here, review if needed
  bgColor?: string;
  title?: string;
  showBackButton?: boolean;
  showRefreshButton?: boolean;
  customAction?: React.ReactNode;
  onRefresh?: () => void;
}

// Copied from PageLayout
const useIOSViewportFix = () => {
  useEffect(() => {
    const setViewportHeight = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    };
    setViewportHeight();
    window.addEventListener('resize', setViewportHeight);
    window.addEventListener('orientationchange', setViewportHeight);
    return () => {
      window.removeEventListener('resize', setViewportHeight);
      window.removeEventListener('orientationchange', setViewportHeight);
    };
  }, []);
};

// This layout enforces a fixed height content area for Chat
export const FixedChatLayout: React.FC<FixedChatLayoutProps> = ({
  children,
  withPadding = false, // Chat usually doesn't need padding here
  bgColor = 'bg-bg-light dark:bg-bg-dark',
  title,
  showBackButton = false,
  showRefreshButton = true,
  customAction,
  onRefresh,
}) => {
  useIOSViewportFix();
  const { t } = useTranslation();

  // TitleBar height = h-12 = 3rem
  // Navigation height = h-14 = 3.5rem
  const calculatedHeight = 'calc(calc(var(--vh, 1vh) * 100) - 3rem - 3.5rem)';

  return (
    // Root element: flex column, full calculated height, overflow hidden
    <main
      className={`v0-app ${bgColor} flex flex-col overflow-hidden`}
      style={{
        height: 'calc(var(--vh, 1vh) * 100)', // Full viewport height needed for calc?
        paddingTop: 'env(safe-area-inset-top)',
        paddingBottom: 'env(safe-area-inset-bottom)', // Keep safe areas
        paddingLeft: 'env(safe-area-inset-left)',
        paddingRight: 'env(safe-area-inset-right)'
      }}
    >
      {/* TitleBar: sticky top */}
      <TitleBar
        title={title}
        showBackButton={showBackButton}
        showRefreshButton={showRefreshButton}
        customAction={customAction}
        onRefresh={onRefresh}
      />
      {/* Content area wrapper: FIXED calculated height, overflow hidden */}
      <div
        className={`overflow-hidden w-full max-w-full mx-auto ${
          withPadding ? 'px-4 md:px-6' : '' // Keep possibility of padding
        }`}
        style={{ height: calculatedHeight }}
      >
        {children} {/* Chat.tsx will render here with h-full */}
      </div>
      {/* Navigation is rendered OUTSIDE this layout by PrivateRoute */}
    </main>
  );
};